/**
 * The forecast engine.
 *
 * Every transaction it emits carries a `source` pointing at the document it came
 * from, so the grid is traceable by construction rather than by bolting lookups
 * on afterwards. If a figure cannot name its document, it does not belong here.
 */

import {
  addDays,
  fridayOnOrAfter,
  addMonths,
  addWeeks,
  daysLate,
  epochDay,
  expectedPaymentDate,
  isoDate,
  lastDayOfMonth,
  lastFridayOfMonth,
  parseDate,
  startOfMonth,
  startOfWeekMon,
  todayStr,
} from "./dates.js";

/**
 * The Monday the forecast starts from — the week we are actually in.
 *
 * v1 anchored the grid to the opening date, which only moves when someone
 * reconciles it, so the table sat four months in the past without complaining.
 * The view follows the calendar; the opening balance is only the balance anchor.
 */
export function forecastAnchor(openingDateIso, todayIso = todayStr()) {
  const openingWeek = startOfWeekMon(parseDate(openingDateIso) || parseDate(todayIso));
  const currentWeek = startOfWeekMon(parseDate(todayIso));
  return currentWeek > openingWeek ? currentWeek : openingWeek;
}

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Stages that mean "agreed, just not billable yet".
 *
 * Matched on the label rather than Scoro's opaque `additional9`, which is a
 * position in a list and can be renumbered underneath us.
 */
const AWAITING_PO = /^\s*PO\s*Needed\s*$/i;

// How far back settled movements are shown for context. Four weeks covers the
// history columns the grid can display; beyond that it is just noise.
const HISTORY_CONTEXT_WEEKS = 4;

/**
 * Split a gross amount into net and VAT using the document's own ratio, so a
 * part-paid invoice does not overstate the VAT still to come.
 */
function splitVat(gross, docNet, docVat) {
  const docGross = (Number(docNet) || 0) + (Number(docVat) || 0);
  const share = docGross ? (Number(docVat) || 0) / docGross : 0;
  const vat = round2(gross * share);
  return { net: round2(gross - vat), vat };
}

/**
 * AMEX modelled as a credit facility rather than a cost line.
 *
 * Spend on the card does not leave the bank when incurred — it leaves when the
 * card is paid. Treating charges as ordinary costs would move the money out
 * weeks early and hide the only question that matters: whether the balance is
 * heading for the limit, and when it has to be paid down.
 *
 * Returns the cash payments the card generates, plus the balance over time.
 */
export function amexLedger({
  settings,
  charges,
  payments = [],
  paydownOverrides = {},
  horizonEnd,
  today = todayStr(),
}) {
  // Missing settings must never mean missing money: an early return here once
  // made every AMEX charge vanish from the forecast without a trace. Defaults
  // are assumed and flagged instead.
  const usingDefaults = !settings;
  const cfg = settings || {
    opening_date: today,
    opening_balance: 0,
    statement_day: 1,
    payment_offset_days: 25,
    credit_limit: 0,
    auto_pay_statement: true,
  };

  const openingDate = parseDate(cfg.opening_date) || parseDate(today);
  const statementDay = Math.min(Math.max(cfg.statement_day || 1, 1), 28);
  const offset = cfg.payment_offset_days ?? 25;
  const limit = Number(cfg.credit_limit) || 0;
  const autoPay = cfg.auto_pay_statement !== false;
  // A card does not let a balance run past its limit and settle up later — the
  // next charge is simply declined. Modelling £118,000 on a £35,000 card is
  // modelling something that cannot happen; with this on, the forecast pays it
  // down as it goes and shows the cash cost of doing so.
  const enforceLimit = !!cfg.enforce_limit && limit > 0;
  const BUFFER = limit ? Math.min(1000, limit * 0.05) : 0;

  // One ordered stream of everything that moves the balance. Statement closes
  // are events too, so an automatic settlement is just a payment the card makes
  // for you and lands in the same timeline as one you make yourself.
  const events = [];
  for (const c of charges) {
    const d = parseDate(c.charge_date);
    if (d)
      events.push({
        date: d,
        kind: "charge",
        amount: Number(c.amount) || 0,
        label: c.description,
        docKey: c.docSource && c.docId ? `${c.docSource}:${c.docId}` : null,
      });
  }
  for (const p of payments) {
    const d = parseDate(p.payment_date);
    // A payment made before the balance was stated is already inside that
    // balance. Applying it again would show the card lower than it is, which is
    // the one direction of error a credit limit cannot tolerate.
    if (d && d >= openingDate) {
      events.push({ date: d, kind: "payment", amount: Number(p.amount) || 0, label: p.note, paid: p.paid, id: p.id });
    }
  }
  if (autoPay) {
    let cursor = new Date(openingDate.getFullYear(), openingDate.getMonth(), statementDay);
    if (cursor < openingDate) cursor = addMonths(cursor, 1);
    let guard = 0;
    while (cursor <= horizonEnd && guard++ < 60) {
      events.push({ date: new Date(cursor), kind: "statement" });
      cursor = addMonths(cursor, 1);
    }
  }
  events.sort((a, b) => a.date - b.date || (a.kind === "charge" ? -1 : 1));

  let balance = Number(cfg.opening_balance) || 0;
  const timeline = [{ date: isoDate(openingDate), balance, kind: "opening" }];
  const cashPayments = [];
  const breaches = [];
  // What each document has put on the card since the last statement. A
  // repayment covers many jobs at once, so knowing only which documents it
  // touched is not enough — the share each is responsible for is the useful
  // number.
  let chargesThisStatement = new Map();
  const shareOut = (total) => {
    const charged = [...chargesThisStatement.values()].reduce((t, v) => t + v, 0);
    if (charged <= 0.01) return {};
    const out = {};
    for (const [k, v] of chargesThisStatement) out[k] = round2((v / charged) * total);
    return out;
  };

  // Statements that have closed but not yet been paid.
  //
  // A statement does not clear the card, it bills it. The balance used to be
  // zeroed the moment a statement closed, weeks before the money actually left,
  // so a card sitting well over its limit reported as £0 owed and the breach
  // never appeared. The balance now falls when the payment lands.
  const billed = [];
  const forcedPaydowns = [];
  const billedTotal = () => billed.reduce((t, b) => t + b.amount, 0);
  const settleDue = (upTo) => {
    while (billed.length && billed[0].date <= upTo) {
      const b = billed.shift();
      if (b.amount <= 0.01) continue;
      balance = round2(balance - b.amount);
      timeline.push({
        date: isoDate(b.date),
        balance,
        kind: "payment",
        amount: b.amount,
        label: "statement settled",
      });
    }
  };

  for (const e of events) {
    if (e.date > horizonEnd) break;
    settleDue(e.date);

    if (e.kind === "charge") {
      balance += e.amount;
      timeline.push({ date: isoDate(e.date), balance, kind: "charge", amount: e.amount, label: e.label });
      // The limit is the point of the exercise: a card with £35,000 of room
      // cannot absorb £69,700 of media however evenly it is spread, and the
      // forecast should say so on the date it stops fitting rather than
      // silently carrying an impossible balance.
      if (limit && balance > limit) {
        breaches.push({
          date: isoDate(e.date),
          balance: round2(balance),
          over: round2(balance - limit),
          limit,
          label: e.label,
        });
        if (enforceLimit) {
          // Bring it back under, on the day it would have gone over. Settles
          // anything already billed first, exactly as a real payment does.
          const suggested = round2(balance - (limit - BUFFER));
          // A paydown the business cannot fund is not a plan. Saying so leaves
          // the card over its limit in the forecast, which is the true
          // consequence — the next charge is the one that gets declined.
          const ov = paydownOverrides[isoDate(e.date)];
          const need = ov?.skip ? 0 : ov?.amount != null ? round2(Number(ov.amount)) : suggested;
          forcedPaydowns.push({
            date: isoDate(e.date),
            suggested,
            amount: need,
            skipped: !!ov?.skip,
            overridden: !!ov && !ov.skip && ov.amount != null,
            note: ov?.note || null,
            wouldReach: round2(balance),
            limit,
          });
          let left = need;
          while (left > 0.01 && billed.length) {
            const b = billed[0];
            const take = Math.min(left, b.amount);
            b.amount = round2(b.amount - take);
            b.cash.amount = round2(b.cash.amount - take);
            left = round2(left - take);
            if (b.amount <= 0.01) billed.shift();
          }
          if (need > 0.01) {
            balance = round2(balance - need);
            timeline.push({
              date: isoDate(e.date),
              balance,
              kind: "payment",
              amount: need,
              label: ov?.skip ? "part paydown" : "paid down to stay inside the limit",
            });
            cashPayments.push({
              date: e.date,
              amount: need,
              enforced: true,
              // Enough for the drawer to offer changing or refusing it.
              forced: true,
              wouldReach: round2(balance + need),
              limit,
              note: "paid down to stay inside the limit",
              docKeys: [...chargesThisStatement.keys()],
              // Attributed the same way a statement is: a paydown clears part
              // of the same period's spend.
              docAmounts: shareOut(need),
            });
          }
        }
      }
      // Which documents' spend this statement is carrying. Without it every
      // AMEX payment looks related to every piece of income that ever touched
      // the card, which is how a September repayment lit up for a cost that
      // does not start until the end of October.
      if (e.docKey) {
        chargesThisStatement.set(e.docKey, (chargesThisStatement.get(e.docKey) || 0) + e.amount);
      }
    } else if (e.kind === "payment") {
      // Paying the card settles what it has billed you before it eats into
      // this month's spend. Without this, a payment someone actually made and
      // the modelled settlement of the same statement would both come out of
      // the bank.
      let left = e.amount;
      while (left > 0.01 && billed.length) {
        const b = billed[0];
        const take = Math.min(left, b.amount);
        b.amount = round2(b.amount - take);
        b.cash.amount = round2(b.cash.amount - take);
        left = round2(left - take);
        if (b.amount <= 0.01) billed.shift();
      }
      balance = round2(balance - e.amount);
      timeline.push({ date: isoDate(e.date), balance, kind: "payment", amount: e.amount, label: e.label });
      cashPayments.push({ date: e.date, amount: e.amount, manual: true, paid: !!e.paid, note: e.label, id: e.id });
    } else {
      // Statement close: this period's new spend becomes due `offset` days
      // later. Only what has not already been billed — the rest is on a
      // statement that is still waiting to be paid.
      const due = round2(balance - billedTotal());
      if (due > 0.01) {
        const payDate = addDays(e.date, offset);
        if (payDate <= horizonEnd) {
          const cash = {
            date: payDate,
            amount: due,
            statementDate: isoDate(e.date),
            docKeys: [...chargesThisStatement.keys()],
            // Proportional to what each document charged, because a statement
            // settles a period rather than any one job.
            docAmounts: shareOut(due),
          };
          cashPayments.push(cash);
          billed.push({ date: payDate, amount: due, cash });
        }
        timeline.push({ date: isoDate(e.date), balance, kind: "statement", amount: due });
        chargesThisStatement = new Map();
      }
    }
  }
  settleDue(horizonEnd);

  // The highest the card actually reaches at the end of a day.
  //
  // A charge and the payment forced by it happen on the same date, so reading
  // every intermediate entry would report a peak that existed for no time at
  // all and contradict the paydown sitting beside it.
  const endOfDay = new Map();
  for (const t of timeline) endOfDay.set(t.date, t);
  const peak = [...endOfDay.values()].reduce((m, p) => (p.balance > m.balance ? p : m), timeline[0]);

  /**
   * What would have to be paid, and when, to keep the card inside its limit.
   *
   * Walks the same timeline and proposes a payment the day before the balance
   * would first go over, sized to bring it back to the limit less a small
   * buffer. Proposals only — the card does not pay itself, and a suggested
   * payment that quietly became a forecast line would be inventing cash
   * movements nobody agreed to.
   */
  const suggestions = [];
  if (limit > 0 && !enforceLimit) {
    let running = Number(cfg.opening_balance) || 0;
    let paidSoFar = 0;
    for (const t of timeline) {
      if (t.kind === "opening") continue;
      if (t.kind === "payment") running -= t.amount;
      else if (t.kind === "charge") running += t.amount;
      // A statement bills the card; it does not clear it.
      const effective = running - paidSoFar;
      if (effective > limit) {
        const need = round2(effective - (limit - BUFFER));
        const when = addDays(parseDate(t.date), -1);
        // One suggestion per date, not per charge: five charges in a week that
        // each push it over are one problem needing one payment, and listing
        // them separately reads as five.
        const on = isoDate(when < openingDate ? openingDate : when);
        const existing = suggestions.find((x) => x.date === on);
        if (existing) {
          existing.amount = round2(existing.amount + need);
          existing.reason = `balance would reach ${Math.round(effective)} against a ${limit} limit`;
        } else {
          suggestions.push({
            date: on,
            amount: need,
            reason: `balance would reach ${Math.round(effective)} against a ${limit} limit`,
          });
        }
        paidSoFar += need;
      }
    }
  }

  return {
    payments: cashPayments.filter((p) => p.amount > 0.01),
    timeline,
    breaches,
    usingDefaults,
    limit,
    autoPay,
    enforceLimit,
    forcedPaydowns,
    peak,
    suggestions,
    headroom: limit ? round2(limit - peak.balance) : null,
  };
}

/**
 * Apply on-account payments to a contact's open invoices, oldest first.
 *
 * A client pays their balance, not an invoice — £9,840 from Creams covers some
 * unknown share of eighteen open invoices. Until Scoro allocates it, the
 * forecast would keep expecting the whole balance.
 *
 * Oldest-first because that is how allocation normally lands, and because
 * expecting the oldest debt to clear first is the conservative reading.
 *
 * A payment stops being applied once Scoro has caught up: if the contact's
 * outstanding has already fallen by the payment amount since it was recorded,
 * the credit is in the mirror and applying it again would count it twice.
 */
export function allocateOnAccount(invoices, payments, { today = todayStr(), paidInvoices = [] } = {}) {
  const credit = {};
  const unallocated = {};
  const superseded = [];

  const byContact = {};
  for (const inv of invoices) {
    if (!inv.company_id) continue;
    (byContact[inv.company_id] = byContact[inv.company_id] || []).push(inv);
  }

  /**
   * What Scoro says arrived from this contact on or after a given day.
   *
   * Both mirrors carry receipts: the settled invoices, and the open ones that
   * have been part paid. Together they are the whole of what Scoro has
   * recorded, which is the only fair thing to compare a hand-entered payment
   * against.
   */
  const receiptsByContact = {};
  for (const doc of [...invoices, ...paidInvoices]) {
    if (!doc.company_id || !Array.isArray(doc.receipts)) continue;
    const list = (receiptsByContact[doc.company_id] = receiptsByContact[doc.company_id] || []);
    for (const r of doc.receipts) {
      if (r?.date) list.push({ date: r.date, amount: Number(r.amount) || 0 });
    }
  }
  const recordedSince = (companyId, from) =>
    (receiptsByContact[companyId] || [])
      .filter((r) => !from || r.date >= from)
      .reduce((s, r) => s + r.amount, 0);

  for (const p of payments) {
    if (p.reconciled) continue;
    const open = (byContact[p.scoro_company_id] || [])
      .slice()
      .sort((a, b) => String(a.deadline || "").localeCompare(String(b.deadline || "")));
    const outstandingNow = open.reduce((s, i) => s + (Number(i.outstanding) || 0), 0);

    /**
     * Has Scoro caught up with this payment?
     *
     * The original test asked whether the contact's total outstanding had
     * fallen by the amount paid. For a client billed monthly it almost never
     * had — new invoices raised in the meantime pushed the total straight back
     * up and hid the drop, so a payment entered by hand went on being counted
     * alongside the receipts Scoro had already recorded for the same money.
     *
     * Asking what arrived, rather than what is left, is immune to that: new
     * invoices do not change what came in on a day.
     */
    const scoroHas = recordedSince(p.scoro_company_id, p.paid_date);
    const caughtUp = scoroHas >= Number(p.amount) - 0.5;

    if (
      caughtUp ||
      (p.contact_outstanding_at_entry != null &&
        outstandingNow <= Number(p.contact_outstanding_at_entry) - Number(p.amount) + 0.01)
    ) {
      superseded.push({ ...p, outstandingNow, scoroHas });
      continue;
    }

    let left = Number(p.amount) || 0;
    for (const inv of open) {
      if (left <= 0) break;
      const already = credit[inv.id] || 0;
      const room = (Number(inv.outstanding) || 0) - already;
      if (room <= 0) continue;
      const take = Math.min(room, left);
      credit[inv.id] = already + take;
      left -= take;
    }
    if (left > 0.01) {
      unallocated[p.scoro_company_id] = (unallocated[p.scoro_company_id] || 0) + left;
    }
  }
  return { credit, unallocated, superseded };
}

/**
 * What actually leaves the bank for a piece of attached spend.
 *
 * `amount` is whatever was typed in. If it was the net price — which is how a
 * supplier quotes — VAT is added on top to get the cash figure; otherwise the
 * amount already includes it.
 */
export function grossOfSpend(sp) {
  const amount = Number(sp.amount) || 0;
  if (sp.includes_vat === false || !sp.amount_is_net) return amount;
  return round2(amount * (1 + Number(sp.vat_rate ?? 20) / 100));
}

/**
 * Expand a recurring cost into dated occurrences across the horizon.
 * These come from the bank statement, which is the only place they exist —
 * Xero holds a single repeating-bill template.
 */
function expandRecurring(rc, from, to) {
  const out = [];
  const step =
    rc.frequency === "weekly"
      ? (d) => addWeeks(d, 1)
      : rc.frequency === "quarterly"
        ? (d) => addMonths(d, 3)
        : rc.frequency === "annual"
          ? (d) => addMonths(d, 12)
          : (d) => addMonths(d, 1);

  // Some costs never arrive on a day. Travel, subsistence, ad-hoc software:
  // the monthly figure is real but the date is an invention, and putting a
  // month of it on the 15th makes one week look dreadful and three look fine.
  // Spread lays it evenly across the weeks instead.
  if (rc.month_rule === "spread" && rc.frequency === "monthly") {
    let cursor = fridayOnOrAfter(from);
    let g = 0;
    while (cursor <= to && g++ < 400) {
      out.push(new Date(cursor));
      cursor = addWeeks(cursor, 1);
    }
    return out;
  }

  // Payroll is dated by rule, not by number: "the last Friday" moves every
  // month, and pinning it to a date would drift a week either way.
  if (rc.month_rule === "last_friday" && rc.frequency === "monthly") {
    let m = new Date(from.getFullYear(), from.getMonth(), 1);
    let g = 0;
    while (m <= to && g++ < 500) {
      const when = lastFridayOfMonth(m.getFullYear(), m.getMonth());
      if (when >= from && when <= to) out.push(when);
      m = addMonths(m, 1);
    }
    return out;
  }

  // The last working day of the month.
  //
  // day_of_month is capped at 28 so that February behaves, which makes "the
  // 31st" impossible to write down — and a cost that leaves at month end is
  // common enough to deserve a rule rather than a date three days early.
  //
  // Working day rather than calendar day, because that is when the money
  // actually moves: 31 January 2027 is a Sunday, and nothing leaves a bank
  // account on a Sunday. The schema named this rule long before anything
  // implemented it.
  if (rc.month_rule === "last_working_day" && rc.frequency === "monthly") {
    let m = new Date(from.getFullYear(), from.getMonth(), 1);
    let g = 0;
    while (m <= to && g++ < 500) {
      const when = lastDayOfMonth(m);
      // Back up off the weekend. Bank holidays are not modelled anywhere in
      // this forecast, and guessing at them would be worse than a day out.
      while (when.getDay() === 0 || when.getDay() === 6) when.setDate(when.getDate() - 1);
      if (when >= from && when <= to) out.push(new Date(when));
      m = addMonths(m, 1);
    }
    return out;
  }

  // An annual cost lands in the month it is actually due. Without that it fires
  // in whatever month the forecast happens to start, which for a bill due in
  // May is simply a different bill.
  if (rc.frequency === "annual" && rc.month_of_year) {
    const day = Math.min(rc.day_of_month || 1, 28);
    let y = from.getFullYear();
    let g = 0;
    while (g++ < 20) {
      const when = new Date(y, rc.month_of_year - 1, day);
      if (when > to) break;
      if (when >= from) out.push(when);
      y += 1;
    }
    return out;
  }

  let cursor;
  if (rc.frequency === "weekly") {
    cursor = new Date(from);
  } else {
    const day = Math.min(rc.day_of_month || 1, 28);
    cursor = new Date(from.getFullYear(), from.getMonth(), day);
    if (cursor < from) cursor = step(cursor);
  }
  let guard = 0;
  while (cursor <= to && guard++ < 500) {
    out.push(new Date(cursor));
    cursor = step(cursor);
  }
  return out;
}

/**
 * Build every dated cash movement in the horizon.
 *
 * Non-GBP documents are skipped rather than converted: there are no FX rates
 * here, and inventing one to make a sterling balance look complete would be
 * worse than a figure that is honestly incomplete. The count is surfaced.
 */
export function buildTransactions({
  scoroInvoices = [],
  scoroBills = [],
  xeroBills = [],
  xeroPaidBills = [],
  expectedDates = {},
  docSpend = [],
  recurring = [],
  recurringOverrides = {},
  amexSettings = null,
  amexCharges = [],
  amexPayments = [],
  paydownOverrides = {},
  fxRates = {},
  historyWeeks = HISTORY_CONTEXT_WEEKS,
  quotes = [],
  quoteStatuses = {},
  quoteRules = {},
  quoteOverrides = {},
  onAccount = [],
  paidInvoices = [],
  instalments = [],
  retainers = [],
  retainerSettings = {},
  paidMarks = {},
  dismissals = {},
  manualEntries = [],
  openingDateIso,
  projectionWeeks = 26,
  today = todayStr(),
}) {
  const anchor = forecastAnchor(openingDateIso, today);
  const end = addWeeks(anchor, projectionWeeks + 4);
  const openingDateForDocs = parseDate(openingDateIso);

  /**
   * Money that moved just before the opening balance was struck.
   *
   * It is inside that balance already, so it must never touch the arithmetic —
   * but the first week of using this is also the week you most want to see,
   * and a blank row where you know a payment went out reads as a bug. These
   * come through flagged as history: shown, marked settled, and excluded from
   * every total.
   */
  // How far back settled things are carried. Four weeks is enough context for
  // a forecast; looking further back is a different question and asks for it
  // explicitly.
  const historyFrom = addWeeks(anchor, -Math.max(HISTORY_CONTEXT_WEEKS, historyWeeks));
  const isHistoryWindow = (d) =>
    openingDateForDocs && d <= openingDateForDocs && d >= historyFrom;
  const txns = [];
  // Money in another currency is still money. Skipping it outright quietly
  // dropped three Spyscape invoices worth $17,866 out of a cash forecast with
  // nothing on screen to say so. Converted where a rate is set; counted and
  // named where one is not, because silence is the one response that cannot be
  // right.
  const excluded = { nonGbp: 0, currencies: {} };
  const rateFor = (ccy) => {
    if (!ccy || ccy === "GBP") return 1;
    const r = Number(fxRates?.[ccy]);
    return r > 0 ? r : null;
  };

  const allocation = allocateOnAccount(scoroInvoices, onAccount, { today, paidInvoices });

  // A document with a payment plan is forecast by its instalments instead of
  // its balance — never both, or the same money is counted twice.
  const planByDoc = {};
  for (const it of instalments) {
    const key = `${it.doc_source}:${it.doc_id}`;
    (planByDoc[key] = planByDoc[key] || []).push(it);
  }

  const pushDoc = (original, kind) => {
    const ccy = original.currency || "GBP";
    const fx = rateFor(ccy);
    if (fx == null) {
      const value = Number(original.outstanding) || 0;
      excluded.nonGbp++;
      excluded.currencies[ccy] = (excluded.currencies[ccy] || 0) + value;
      return;
    }
    // Converted once, here, so everything downstream works in one currency and
    // no later calculation has to remember which document was foreign.
    const doc =
      fx === 1
        ? original
        : {
            ...original,
            outstanding: round2((Number(original.outstanding) || 0) * fx),
            sum: round2((Number(original.sum) || 0) * fx),
            vat_sum: round2((Number(original.vat_sum) || 0) * fx),
            foreign: {
              currency: ccy,
              rate: fx,
              outstanding: Number(original.outstanding) || 0,
            },
          };
    // The kind carries where the document came from as well as which way the
    // money goes, so a Xero bill's marks and plans are filed against it rather
    // than against a Scoro bill that happens to share an id.
    const docType =
      kind === "invoice" ? "scoro_invoice" : kind === "xero_bill" ? "xero_bill" : "scoro_bill";
    const planKey = `${docType}:${doc.id}`;
    // Dismissed: still in Scoro, deliberately kept out of the forecast.
    if (dismissals[planKey]) return;

    // Marked paid by hand: it has left the bank, whatever Scoro still thinks.
    // Dated on the day it actually went, not the day it was expected.
    const mark = paidMarks[planKey];
    if (mark) {
      const when = parseDate(mark.paid_date);
      const gross = Number(doc.outstanding) || 0;
      if (when && when <= end && gross) {
        const income = kind === "invoice";
        txns.push({
          key: `paid-${planKey}`,
          date: when,
          amount: income ? gross : -gross,
          label: doc.company_name || (income ? "Invoice" : "Bill"),
          category: income ? "income" : "suppliers",
          isReceived: true,
          source: {
            type: docType,
            id: String(doc.id),
            docNo: doc.no,
            contact: doc.company_name,
            description: doc.description,
            issued: doc.date,
            dueDate: doc.deadline,
            gross: Number(doc.sum || 0) + Number(doc.vat_sum || 0),
            outstanding: gross,
            paidDate: mark.paid_date,
            markedByHand: true,
            markNote: mark.note,
            expectedDate: mark.paid_date,
          },
        });
      }
      return;
    }
    const plan = planByDoc[planKey];
    if (plan?.length) {
      const income = kind === "invoice";
      for (const it of plan) {
        const when = parseDate(it.due_date);
        if (!when || when > end) continue;
        const instBefore = openingDateForDocs && when <= openingDateForDocs;
        if (instBefore && !(it.paid && isHistoryWindow(when))) continue;
        const amount = Number(it.amount) || 0;
        if (!amount) continue;
        txns.push({
          key: `instalment-${it.id}`,
          date: when,
          amount: income ? amount : -amount,
          label: doc.company_name || (income ? "Invoice" : "Bill"),
          category: income ? "income" : "suppliers",
          isReceived: !!it.paid,
          isHistory: !!instBefore,
          source: {
            type: income ? "scoro_invoice" : docType,
            id: String(doc.id),
            docNo: doc.no,
            contact: doc.company_name,
            description: doc.description,
            issued: doc.date,
            dueDate: doc.deadline,
            daysLate: daysLate(doc.deadline, today),
            gross: Number(doc.sum || 0) + Number(doc.vat_sum || 0),
            outstanding: Number(doc.outstanding) || 0,
            onPaymentPlan: true,
            instalmentNote: it.note,
            paidDate: it.paid ? it.due_date : null,
            expectedDate: it.due_date,
          },
        });
      }
      return;
    }

    const covered = kind === "invoice" ? allocation.credit[doc.id] || 0 : 0;
    const gross = (Number(doc.outstanding) || 0) - covered;
    // Near zero is not future cash: an invoice fully covered by money already
    // received has nothing left to arrive.
    //
    // A negative is a credit note, and it is very much future cash — it is
    // money this client will not now be sending. Dropping those alongside the
    // settled ones had the forecast quietly counting income that had been
    // credited back.
    if (Math.abs(gross) <= 0.01) return;
    const key = `${kind}:${doc.id}`;
    // A credit note rides on the client's next receipt, because that is where
    // it is actually applied. Failing that it is reported rather than invented
    // — see below.
    const expected =
      gross < 0
        ? expectedDates[key] || nextReceiptByContact[doc.company_id] || null
        : expectedPaymentDate(doc.deadline, expectedDates[key], today);
    const d = parseDate(expected);

    // Overdue, and nobody has said when it will actually move.
    //
    // It is not forecast — a date the engine invented is not information — but
    // it is very much still owed, so it is counted and reported. Leaving it out
    // silently would understate both what is coming in and what has to go out.
    //
    // A credit note is exempt. It is not money somebody is waiting to receive,
    // it is a reduction that will be applied, and dropping it for want of a
    // date makes income look higher than it is — the one direction a forecast
    // must never drift on its own.
    // Reported, not guessed. A credit note against a client with nothing else
    // expected has no receipt to reduce, and putting it on a date of our own
    // choosing would show money moving that never moves.
    if (!expected && doc.deadline) {
      const side = kind === "invoice" ? "in" : "out";
      excluded.unplanned = excluded.unplanned || { in: { count: 0, total: 0 }, out: { count: 0, total: 0 } };
      excluded.unplanned[side].count += 1;
      excluded.unplanned[side].total += Math.abs(gross);
      return;
    }
    if (!d || d > end) return;
    const income = kind === "invoice";
    const { net, vat } = splitVat(gross, doc.sum, doc.vat_sum);

    txns.push({
      key: `scoro-${key}`,
      date: d,
      amount: income ? gross : -gross,
      label: doc.company_name || (income ? "Scoro invoice" : "Scoro bill"),
      category: income ? "income" : "suppliers",
      net: income ? net : -net,
      vat: income ? vat : -vat,
      source: {
        type: income ? "scoro_invoice" : docType,
        id: String(doc.id),
        docNo: doc.no,
        contact: doc.company_name,
        description: doc.description,
        issued: doc.date,
        dueDate: doc.deadline,
        daysLate: daysLate(doc.deadline, today),
        gross: Number(doc.sum || 0) + Number(doc.vat_sum || 0),
        paid: Number(doc.paid_sum) || 0,
        outstanding: gross,
        onAccountCovered: covered,
        expectedDate: expected,
        expectedIsOverride: !!expectedDates[key],
      },
    });
  };

  /**
   * When each client's next money is expected.
   *
   * A credit note is not a payment. Nobody writes a cheque for one: it reduces
   * what the client owes and is netted off their next invoice. Dating it to
   * "the next payment run" put £7,088 of Trek and Bear credits onto a single
   * Friday as though the money were moving that day, which is not a thing that
   * happens.
   *
   * Landing it with the client's next receipt is what actually occurs, and it
   * keeps the arithmetic honest: the receipt arrives net of the credit rather
   * than the credit floating free.
   */
  const nextReceiptByContact = {};
  for (const inv of scoroInvoices) {
    const gross = (Number(inv.outstanding) || 0);
    if (gross <= 0 || !inv.company_id) continue;
    const when = expectedPaymentDate(inv.deadline, expectedDates[`invoice:${inv.id}`], today);
    if (!when) continue;
    const seen = nextReceiptByContact[inv.company_id];
    if (!seen || when < seen) nextReceiptByContact[inv.company_id] = when;
  }

  for (const d of scoroInvoices) pushDoc(d, "invoice");
  for (const d of scoroBills) pushDoc(d, "bill");

  // Xero owns what goes out. Its bills are reshaped to look like a Scoro
  // document rather than given a path of their own, so everything already
  // built — moving a date, marking one paid, agreeing a payment plan, the
  // drawer, the bulk panel — works on them without knowing where they came
  // from. A merged bill is one somebody typed in by hand before Xero was
  // connected; the typed one stops counting, not this.
  for (const b of xeroBills) {
    pushDoc(
      {
        id: b.id,
        no: b.number || b.reference,
        company_id: b.contact_id,
        company_name: b.contact_name,
        currency: b.currency,
        sum: Number(b.sub_total) || 0,
        vat_sum: Number(b.total_tax) || 0,
        paid_sum: Number(b.amount_paid) || 0,
        outstanding: Number(b.amount_due) || 0,
        date: b.date,
        deadline: b.due_date,
        status: b.status,
        description: b.source_note || b.reference || null,
      },
      "xero_bill"
    );
  }

  // On-account money as actual cash in.
  //
  // The credit reduces what the invoices still expect; this is the other half —
  // the receipt itself. Whether it belongs in the forecast depends entirely on
  // the opening balance date: cash that arrived BEFORE the opening balance was
  // struck is already inside that figure, and adding it again would count it
  // twice. Cash that arrived after it is genuinely still to come as far as the
  // forecast is concerned, and must show.
  //
  // Emitted regardless of whether the credit is still being applied: once Scoro
  // allocates the payment the invoices drop by themselves, but the money still
  // landed in the bank on the day it landed.
  const openingDate = parseDate(openingDateIso);
  const supersededIds = new Set(allocation.superseded.map((p) => p.id));
  for (const p of onAccount) {
    const when = parseDate(p.paid_date);
    if (!when || when > end) continue;
    const oaBefore = openingDate && when <= openingDate;
    if (oaBefore && !isHistoryWindow(when)) continue;
    // Once Scoro has recognised the payment it arrives as a settled invoice
    // instead, so keeping this entry too would count the same money twice.
    if (supersededIds.has(p.id) || p.reconciled) continue;
    const amount = Number(p.amount) || 0;
    if (!amount) continue;
    const income = (p.direction || "in") === "in";
    txns.push({
      key: `on-account-${p.id}`,
      date: when,
      amount: income ? amount : -amount,
      label: p.contact_name || "On-account payment",
      category: income ? "income" : "suppliers",
      // Recorded on-account money has, by definition, already arrived — that is
      // why someone typed it in. It should read as received, not as a forecast.
      isReceived: p.received !== false,
      isHistory: !!oaBefore,
      source: {
        type: "on_account_payment",
        id: p.id,
        contact: p.contact_name,
        note: p.note,
        paidDate: p.paid_date,
        reconciled: p.reconciled,
        outstandingAtEntry: p.contact_outstanding_at_entry,
      },
    });
  }

  // Money that has actually arrived.
  //
  // The open mirror drops an invoice the moment it is paid, so without this a
  // settled invoice simply vanishes from the week it was expected in — the one
  // question everybody asks of a forecast ("did that land?") had no answer.
  //
  // Same opening-balance rule as everything else: cash received before the
  // opening balance is already inside it.
  for (const inv of paidInvoices) {
    const rfx = rateFor(inv.currency || "GBP");
    if (rfx == null) continue;
    const gross = round2(((Number(inv.sum) || 0) + (Number(inv.vat_sum) || 0)) * rfx);
    if (!gross) continue;

    // One movement per receipt. An invoice settled in pieces is several
    // arrivals of money on several days, and collapsing it into its total on
    // one date put £4,700 of July into September. Where the receipts are not
    // known, the invoice still counts once on its recorded date.
    const pieces = Array.isArray(inv.receipts) && inv.receipts.length
      ? inv.receipts.map((r) => ({ date: parseDate(r.date), amount: round2((Number(r.amount) || 0) * rfx) }))
      : [{ date: parseDate(inv.paid_date), amount: gross }];

    pieces.forEach((piece, i) => {
      const when = piece.date;
      if (!when || when > end || !piece.amount) return;
      const invBefore = openingDate && when <= openingDate;
      if (invBefore && !isHistoryWindow(when)) return;
      const share = piece.amount / gross;

      txns.push({
        key: `scoro-received-${inv.id}${pieces.length > 1 ? `-${i}` : ""}`,
        date: when,
        amount: piece.amount,
        label: inv.company_name || "Received",
        category: "income",
        isReceived: true,
        isHistory: !!invBefore,
        net: round2((Number(inv.sum) || 0) * share),
        source: {
          type: "scoro_received",
          id: String(inv.id),
          docNo: inv.no,
          contact: inv.company_name,
          description: inv.description,
          issued: inv.date,
          dueDate: inv.deadline,
          paidDate: isoDate(when),
          gross,
          received: piece.amount,
          partOf: pieces.length > 1 ? `${i + 1} of ${pieces.length} payments` : null,
        },
      });
    });
  }

  // Money that arrived against an invoice that is still open.
  //
  // A part payment is cash in the bank on the day it landed, but the invoice
  // it belongs to stays in the open mirror until the balance is cleared, so
  // none of it showed anywhere. The remaining balance is forecast separately
  // from `outstanding`, which already nets this off, so there is no doubling.
  for (const inv of scoroInvoices) {
    if (!Array.isArray(inv.receipts) || !inv.receipts.length) continue;
    const rfx = rateFor(inv.currency || "GBP");
    if (rfx == null) continue;
    inv.receipts.forEach((r, i) => {
      const when = parseDate(r.date);
      const amount = round2((Number(r.amount) || 0) * rfx);
      if (!when || when > end || !amount) return;
      const before = openingDate && when <= openingDate;
      if (before && !isHistoryWindow(when)) return;
      txns.push({
        key: `scoro-part-${inv.id}-${i}`,
        date: when,
        amount,
        label: inv.company_name || "Received",
        category: "income",
        isReceived: true,
        isHistory: !!before,
        source: {
          type: "scoro_received",
          id: String(inv.id),
          docNo: inv.no,
          contact: inv.company_name,
          description: inv.description,
          issued: inv.date,
          dueDate: inv.deadline,
          paidDate: isoDate(when),
          gross: round2(((Number(inv.sum) || 0) + (Number(inv.vat_sum) || 0)) * rfx),
          received: amount,
          partOf: "part payment · the balance is still outstanding",
        },
      });
    });
  }

  // Money that has actually gone out.
  //
  // The mirror of open bills drops one the moment it is paid, so without this
  // a settled bill simply disappears — three bills paid on 9 September could
  // not be found anywhere in the system, and every past week showed money
  // arriving and none leaving.
  //
  // One movement per payment, on the day it went, for the same reason the
  // income side does it: a bill settled in pieces is several days of cash, and
  // collapsing it onto one date puts money in the wrong week.
  for (const bill of xeroPaidBills) {
    const rfx = rateFor(bill.currency || "GBP");
    if (rfx == null) continue;
    const gross = round2((Number(bill.total) || 0) * rfx);
    const pieces =
      Array.isArray(bill.payments) && bill.payments.length
        ? bill.payments.map((p) => ({ date: parseDate(p.date), amount: round2((Number(p.amount) || 0) * rfx) }))
        : [{ date: parseDate(bill.paid_date), amount: gross }];

    pieces.forEach((piece, i) => {
      const when = piece.date;
      if (!when || when > end || !piece.amount) return;
      const before = openingDate && when <= openingDate;
      if (before && !isHistoryWindow(when)) return;
      txns.push({
        key: `xero-paid-${bill.id}${pieces.length > 1 ? `-${i}` : ""}`,
        date: when,
        amount: -piece.amount,
        label: bill.contact_name || "Paid",
        category: "suppliers",
        isReceived: true,
        isHistory: !!before,
        source: {
          type: "xero_paid",
          id: String(bill.id),
          docNo: bill.number || bill.reference,
          contact: bill.contact_name,
          description: bill.reference,
          issued: bill.date,
          dueDate: bill.due_date,
          paidDate: isoDate(when),
          gross,
          paid: piece.amount,
          partOf: pieces.length > 1 ? `${i + 1} of ${pieces.length} payments` : null,
        },
      });
    });
  }

  // Money already gone against a bill that is still open. The remaining
  // balance is forecast separately from AmountDue, which nets this off, so
  // there is no doubling.
  for (const bill of xeroBills) {
    if (!Array.isArray(bill.payments) || !bill.payments.length) continue;
    const rfx = rateFor(bill.currency || "GBP");
    if (rfx == null) continue;
    bill.payments.forEach((p, i) => {
      const when = parseDate(p.date);
      const amount = round2((Number(p.amount) || 0) * rfx);
      if (!when || when > end || !amount) return;
      const before = openingDate && when <= openingDate;
      if (before && !isHistoryWindow(when)) return;
      txns.push({
        key: `xero-part-${bill.id}-${i}`,
        date: when,
        amount: -amount,
        label: bill.contact_name || "Paid",
        category: "suppliers",
        isReceived: true,
        isHistory: !!before,
        source: {
          type: "xero_paid",
          id: String(bill.id),
          docNo: bill.number || bill.reference,
          contact: bill.contact_name,
          issued: bill.date,
          dueDate: bill.due_date,
          paidDate: isoDate(when),
          gross: round2((Number(bill.total) || 0) * rfx),
          paid: amount,
          partOf: "part payment · the balance is still outstanding",
        },
      });
    });
  }

  // Cash no document produces: lending, grants, a VAT refund, a one-off cost.
  // Labelled manual so it is never mistaken for something traceable.
  /**
   * What VAT each client is actually charged, from their own invoices.
   *
   * A hand-entered line states an amount and nothing else, so the net figure
   * behind it has to come from somewhere. Guessing the standard rate was wrong
   * for the clients who are not charged it — Nielsen-Massey is zero-rated on
   * every invoice they have ever had, and so is Spyscape.
   *
   * Reading it from the documents is right almost always, needs no maintenance,
   * and corrects itself if a client's treatment changes. A rate set on the line
   * overrules it, for whatever the history cannot know.
   */
  const vatRateByClient = {};
  for (const doc of [...scoroInvoices, ...paidInvoices]) {
    const netSum = Number(doc.sum) || 0;
    if (!doc.company_name || !netSum) continue;
    const rate = Math.round(((Number(doc.vat_sum) || 0) / netSum) * 100);
    const seen = (vatRateByClient[doc.company_name] = vatRateByClient[doc.company_name] || {});
    seen[rate] = (seen[rate] || 0) + 1;
  }
  const vatRateFor = (name) => {
    const seen = vatRateByClient[name];
    if (!seen) return 20;
    const [rate] = Object.entries(seen).sort((a, b) => b[1] - a[1])[0];
    return Number(rate);
  };

  for (const m of manualEntries) {
    // Superseded by the real bill from Xero. It stays on record — and keeps
    // its planned payment date, which moved across to the bill — but counting
    // both would be counting the same money twice.
    if (m.merged_into) continue;
    const rawWhen = parseDate(m.entry_date);
    if (!rawWhen || rawWhen > end) continue;
    const beforeOpening = openingDate && rawWhen <= openingDate;

    // Three different things can be true of a date before the opening balance,
    // and only one of them means "ignore it".
    //
    //   paid, recently      -> history: shown in grey, never counted
    //   paid, long ago      -> nothing to show
    //   NOT paid            -> still owed. The date has passed but the money
    //                          has not moved, so it rolls forward like an
    //                          overdue bill. Dropping it silently lost a real
    //                          liability — a PAYE payment simply vanished.
    let when = rawWhen;
    let rolledForward = false;
    if (beforeOpening) {
      if (m.received) {
        if (!isHistoryWindow(rawWhen)) continue;
      } else {
        when = fridayOnOrAfter(parseDate(today));
        rolledForward = true;
      }
    }
    const amount = Number(m.amount) || 0;
    if (!amount) continue;
    const income = (m.direction || "in") === "in";

    txns.push({
      key: `manual-${m.id}`,
      date: when,
      amount: income ? amount : -amount,
      label: m.label,
      // Money lent to the business is income, but it is not revenue and should
      // never be read as trading. It keeps its own category so the grid can
      // stand it apart from the clients.
      category: income ? (m.category === "lending" ? "lending" : "income") : m.category || "other",
      isLending: income && m.category === "lending",
      // What the work is worth before VAT. Lending and tax are not sales, so
      // there is no VAT in them to take out.
      net: (() => {
        if (!income) return undefined;
        if (m.category === "lending" || m.category === "tax") return amount;
        const rate = m.vat_rate != null ? Number(m.vat_rate) : vatRateFor(m.label);
        return round2(amount / (1 + rate / 100));
      })(),
      vatRate: income
        ? m.vat_rate != null
          ? Number(m.vat_rate)
          : m.category === "lending" || m.category === "tax"
            ? 0
            : vatRateFor(m.label)
        : undefined,
      vatStated: income ? m.vat_rate != null : undefined,
      isReceived: !!m.received,
      isHistory: !!beforeOpening && !rolledForward,
      // Only entries that ask for their own row get one; imported bills sit in
      // their category with everything else.
      isManual: m.own_row !== false,
      source: {
        type: "manual_entry",
        id: m.id,
        contact: m.label,
        description: m.note,
        category: m.category,
        direction: m.direction,
        received: m.received,
        rolledForward,
        // The work this line delivers, where it was typed in rather than
        // attached to a document.
        linkedDoc: m.linked_doc || null,
        originalDate: rolledForward ? m.entry_date : null,
        expectedDate: isoDate(when),
      },
    });
  }

  // Retainers.
  //
  // Scoro models these as projects, and holds no periodic value on them, so the
  // monthly figure comes from what has actually been invoiced against each one.
  //
  // A retainer whose term has ended is NOT forecast: seven of them sit "In
  // Progress" in Scoro with terms that expired, and rolling those forward would
  // invent income from contracts that may never have been renewed. Extending
  // the end date is a decision someone has to take deliberately.
  // How long each client actually takes to pay, from their own settled
  // invoices. A retainer billed on the 1st is not cash on the 1st — Bear pay in
  // about 26 days, Peter's Yard in 42 — and treating the billing date as the
  // receipt date pulls a month of income forward across the whole book.
  const payLagByClient = {};
  {
    const lags = {};
    for (const p of paidInvoices) {
      const raised = parseDate(p.date);
      const settled = parseDate(p.paid_date);
      if (!raised || !settled || !p.company_name) continue;
      const days = Math.round((settled - raised) / 86400000);
      if (days < 0 || days > 400) continue;
      (lags[p.company_name] = lags[p.company_name] || []).push(days);
    }
    for (const [name, list] of Object.entries(lags)) {
      // Three settled invoices is the least that makes a median mean anything.
      if (list.length < 3) continue;
      list.sort((x, y) => x - y);
      payLagByClient[name] = list[Math.floor(list.length / 2)];
    }
  }

  for (const r of retainers) {
    const cfg = retainerSettings[r.project_id] || {};
    if (cfg.include === false) continue;
    // The fee is net; VAT is added on top unless the client is outside the UK.
    // Working in gross made a 20% difference invisible and impossible to state.
    const netMonthly =
      Number(
        cfg.monthly_net_override ??
          r.derived_monthly_net ??
          // Older rows only carried gross. Assume the VAT that was observed.
          (r.derived_monthly != null && r.vat_observed !== false
            ? r.derived_monthly / 1.2
            : r.derived_monthly)
      ) || 0;
    const vatRate =
      cfg.vat_rate != null ? Number(cfg.vat_rate) : r.vat_observed === false ? 0 : 20;
    const monthly = round2(netMonthly * (1 + vatRate / 100));
    if (!monthly) continue;

    // A blank end date means two different things. On a Scoro project it is
    // missing data, and rolling it forward would invent income. On one someone
    // typed in it means "they pay us monthly", which is a statement, so it runs
    // to the edge of the forecast.
    const termEnd =
      parseDate(cfg.end_override || r.term_end) || (r.source === "manual" ? end : null);
    if (!termEnd || termEnd < anchor) continue; // expired: needs a human, not a default

    const day = Math.min(Math.max(Number(cfg.day_of_month) || 1, 1), 28);
    let cursor = new Date(anchor.getFullYear(), anchor.getMonth(), day);
    if (cursor < anchor) cursor = addMonths(cursor, 1);

    // Months that have already been invoiced are in the forecast as real
    // receivables. Repeating the retainer over the top of them would count the
    // same money twice, so the line starts the month after the last invoice
    // actually raised against the project — and never before next month, since
    // the current month's retainer invoices have already gone out.
    const now = parseDate(todayStr());
    let firstClear = new Date(now.getFullYear(), now.getMonth() + 1, day);
    const invoicedTo = parseDate(cfg.invoiced_to_override || r.invoiced_to);
    if (invoicedTo) {
      const afterInvoiced = new Date(invoicedTo.getFullYear(), invoicedTo.getMonth() + 1, day);
      if (afterInvoiced > firstClear) firstClear = afterInvoiced;
    }
    if (cursor < firstClear) cursor = firstClear;
    let guard = 0;
    const lag =
      cfg.payment_lag_days != null
        ? Number(cfg.payment_lag_days)
        : (payLagByClient[r.company_name] ?? 30);

    while (cursor <= end && cursor <= termEnd && guard++ < 36) {
      const billed = new Date(cursor);
      cursor = addMonths(cursor, 1);
      // Same convention as a real invoice: money lands on the Friday of the
      // week it is due.
      const when = fridayOnOrAfter(addDays(billed, lag));
      if (when > end) continue;
      txns.push({
        key: `retainer-${r.project_id}-${isoDate(billed)}`,
        date: when,
        amount: monthly,
        label: r.company_name || r.name,
        category: "income",
        isRetainer: true,
        net: netMonthly,
        source: {
          type: "scoro_retainer",
          id: String(r.project_id),
          contact: r.company_name,
          description: r.name,
            termStart: r.term_start,
          termEnd: cfg.end_override || r.term_end,
          monthly,
          netMonthly,
          vatRate,
          derivedFrom:
            cfg.monthly_override != null
              ? "set by hand"
              : `${r.invoice_count} invoices over ${r.months_observed} months`,
          invoicedTo: cfg.invoiced_to_override || r.invoiced_to || null,
          expectedDate: isoDate(when),
          billedDate: isoDate(billed),
          paymentLagDays: lag,
          lagSource:
            cfg.payment_lag_days != null
              ? "set by hand"
              : payLagByClient[r.company_name] != null
                ? "their usual time to pay"
                : "30 days assumed",
        },
      });
    }
  }

  // Pipeline: quotes that a rule says to include, at the probability that rule
  // carries. Off by default — a quote is not cash, and nothing should appear in
  // a cash forecast because a default said so.
  //
  // Dated on estimated_closing_date where Scoro has one, since that is someone's
  // actual judgement about when it lands; the quote deadline is the fallback.
  const quotesInForecast = new Set();
  // Work that was won. The quote no longer produces income — the invoice it
  // became does — but everything attached to it is now certain rather than
  // speculative, which is the opposite of a reason to drop it.
  const quotesWon = new Set(
    quotes.filter((q) => String(q.status) === "completed").map((q) => String(q.id))
  );
  // Every quote we still know anything about, won or not, so a cost whose
  // quote has left the mirror entirely can be told apart from one whose quote
  // is present and simply not forecast.
  const quotesKnown = new Set(quotes.map((q) => String(q.id)));
  for (const q of quotes) {
    // Won work is never forecast twice.
    //
    // A completed quote has become an invoice, and the invoice is already in
    // the forecast carrying the same money. Completed quotes used to be thrown
    // away by the sync, so this could not arise; keeping them so their costs
    // survive brought it into reach, and an "include this one" set while the
    // quote was still live went on applying after it was won. Bighams was
    // counted twice at £48,000 that way.
    //
    // This deliberately overrides the per-quote decision rather than deferring
    // to it. The decision was made about a live quote and the quote is no
    // longer live; honouring it means honouring an answer to a question nobody
    // is asking any more.
    if (String(q.status) === "completed") continue;

    const rule = quoteRules[q.status];
    const own = quoteOverrides[q.id];
    // A decision about this specific quote always beats the stage default —
    // including a deliberate "not this one" for a quote whose stage is on.
    const include = own ? own.include : !!rule?.include;
    if (!include) continue;
    const qccy = q.currency || "GBP";
    const qfx = rateFor(qccy);
    if (qfx == null) {
      excluded.nonGbp++;
      excluded.currencies[qccy] =
        (excluded.currencies[qccy] || 0) + (Number(q.sum) || 0) + (Number(q.vat_sum) || 0);
      continue;
    }
    quotesInForecast.add(String(q.id));
    const when = parseDate(
      own?.expected_date || q.estimated_closing_date || q.deadline || q.date
    );
    if (!when || when > end) continue;
    const full = round2(((Number(q.sum) || 0) + (Number(q.vat_sum) || 0)) * qfx);
    const pct = Number(own?.probability ?? rule?.probability ?? 100) / 100;
    const amount = round2(full * pct);
    if (!amount) continue;

    // A retainer quote describes one month of an ongoing arrangement, not a
    // one-off, so forecasting it once understates every month after it. Repeat
    // is a property of the stage, since "Retainer" is what makes it true.
    const dates = [];
    if (rule?.repeat_monthly) {
      // Roll forward to the current month before repeating. A retainer quote
      // raised in January is still live, but its January date is history — the
      // useful statement is "this happens every month from now on", not a run of
      // lines in the past that no forecast window will ever show.
      let cursor = new Date(when);
      let guard = 0;
      while (cursor < anchor && guard++ < 60) cursor = addMonths(cursor, 1);
      guard = 0;
      while (cursor <= end && guard++ < 36) {
        dates.push(new Date(cursor));
        cursor = addMonths(cursor, 1);
      }
    } else {
      dates.push(when);
    }
    if (!dates.length) continue;

    dates.forEach((d, i) =>
    txns.push({
      key: `scoro-quote-${q.id}${i ? `-m${i}` : ""}`,
      date: d,
      amount,
      label: q.company_name || q.quote_name || "Quote",
      category: "income",
      isPipeline: true,
      // "PO Needed" is not speculation. The work is agreed and the client has
      // said yes — what is missing is a purchase order number before it can be
      // billed. Showing it beside genuinely unwon proposals misreads the book.
      isAwaitingPo: AWAITING_PO.test(quoteStatuses[q.status]?.label || ""),
      net: round2((Number(q.sum) || 0) * pct),
      source: {
        type: "scoro_quote",
        id: String(q.id),
        docNo: q.no,
        contact: q.company_name,
        description: q.description,
        quoteName: q.quote_name,
        issued: q.date,
        dueDate: q.deadline,
        stage: quoteStatuses[q.status]?.label || q.status,
        statusKey: String(q.status || ""),
        stageColour: quoteStatuses[q.status]?.colour || null,
        fullValue: full,
        probability: Number(own?.probability ?? rule?.probability ?? 100),
        includedBy: own ? "this quote" : `stage rule (${quoteStatuses[q.status]?.label || q.status})`,
        expectedDate: isoDate(d),
        expectedIsOverride: !!own?.expected_date,
        estimatedClosing: q.estimated_closing_date,
        repeatsMonthly: !!rule?.repeat_monthly,
        monthOffset: i,
      },
    })
    );
  }

  // Spend attached to a document.
  //
  // Split evenly across spread_weeks so a media budget lands the way it is
  // actually spent rather than as one lump — a five-week buy that shows as a
  // single outflow misstates four of those weeks. Rounding is settled on the
  // final instalment so the parts always sum to the total.
  //
  // AMEX-flagged lines deliberately do NOT become cash movements here: they
  // feed the card ledger and reach the bank when the statement is paid.
  const amexFromSpend = [];
  for (const sp of docSpend) {
    // A cost attached to a quote lives or dies with the work.
    //
    //   quote forecast          -> the work is expected; so is the cost
    //   quote won (completed)   -> the work is happening; the cost is certain
    //   quote declined/expired  -> no work, no cost
    //   quote gone from Scoro   -> we cannot tell, so we do not guess
    //
    // The last case used to be silently dropped along with the third, which is
    // how £58,000 of supplier spend left the forecast without anybody being
    // told. It is now reported instead: a cost whose quote has disappeared is
    // a question for a person, not a rounding-down.
    if (sp.doc_source === "scoro_quote") {
      const id = String(sp.doc_id);
      const counts = quotesInForecast.has(id) || quotesWon.has(id);
      if (!counts) {
        if (!quotesKnown.has(id)) {
          excluded.orphanedSpend = excluded.orphanedSpend || [];
          excluded.orphanedSpend.push({
            id: sp.id,
            quoteId: id,
            label: sp.supplier_name || sp.description || "Attached spend",
            amount: grossOfSpend(sp),
          });
        } else {
          excluded.spendOnQuotesOut = (excluded.spendOnQuotesOut || 0) + 1;
        }
        continue;
      }
    }
    const weeks = Math.max(1, Number(sp.spread_weeks) || 1);
    // A supplier quotes a price and adds VAT to the invoice, so the number
    // typed in is usually the net one and the bank pays more than it. Gross is
    // what leaves the account, and that is what the forecast is about.
    const total = grossOfSpend(sp);
    const per = round2(total / weeks);
    const from = parseDate(sp.start_date);
    if (!from || !total) continue;

    for (let i = 0; i < weeks; i++) {
      const when = addWeeks(from, i);
      if (when > end) break;
      const amount = i === weeks - 1 ? round2(total - per * (weeks - 1)) : per;
      if (!amount) continue;
      const label = sp.supplier_name || sp.description || "Attached spend";
      // Cash movements are always gross — that is what leaves the account. The
      // net figure travels alongside for margin, where VAT does not belong.
      const rate = Number(sp.vat_rate ?? 20) / 100;
      const netAmount = sp.includes_vat === false ? amount : round2(amount / (1 + rate));


      if (sp.method === "amex") {
        amexFromSpend.push({
          id: `${sp.id}-${i}`,
          amount,
          charge_date: isoDate(when),
          description: label,
          docSource: sp.doc_source,
          docId: sp.doc_id,
          // Carried through so the card can say what a charge is for rather
          // than repeating "Meta" eleven times with no way to tell them apart.
          docLabel: sp.description || null,
        });
        continue;
      }

      txns.push({
        key: `doc-spend-${sp.id}-${i}`,
        date: when,
        amount: -amount,
        label,
        category: sp.method === "payroll" ? "staff" : sp.category || "suppliers",
        net: -netAmount,
        source: {
          type: "doc_spend",
          id: sp.id,
          includesVat: sp.includes_vat !== false,
          netAmount,
          description: sp.description,
          supplier: sp.supplier_name,
          method: sp.method,
          attachedTo: { source: sp.doc_source, id: sp.doc_id },
          instalment: weeks > 1 ? `${i + 1} of ${weeks}` : null,
          totalAmount: total,
          expectedDate: isoDate(when),
        },
      });
    }
  }

  // Recurring costs, split by how they are paid for the same reason.
  const amexFromRecurring = [];
  for (const rc of recurring) {
    if (!rc.active) continue;
    for (const when of expandRecurring(rc, anchor, end)) {
      // A standing cost is a pattern, not a promise. September's payroll being
      // higher than usual is a fact about September, so it is recorded against
      // that date rather than by editing what payroll normally is.
      const ov = recurringOverrides[`${rc.id}:${isoDate(when)}`];
      if (ov?.skip) continue;
      // Spread costs are stated per month but paid across it, so each weekly
      // slice is the monthly figure annualised and divided by the 52 weeks it
      // is actually spread over.
      const spread = rc.month_rule === "spread" && rc.frequency === "monthly";
      const base = Number(rc.amount) || 0;
      const amountFor =
        ov?.amount != null ? Number(ov.amount) : spread ? round2((base * 12) / 52) : base;
      if (!amountFor) continue;
      if (rc.payment_method === "amex") {
        amexFromRecurring.push({
          id: `${rc.id}-${isoDate(when)}`,
          amount: amountFor,
          charge_date: isoDate(when),
          description: rc.payee,
        });
        continue;
      }
      // Already inside the opening balance.
      //
      // Documents and hand-entered lines have always respected this; standing
      // costs never did, because when the opening balance was struck by hand
      // months back it made no difference. Anchoring to the bank every night
      // made it the whole difference: payroll that left this morning is in the
      // measured balance, and was being forecast to leave again tonight.
      const beforeOpening = openingDate && when <= openingDate;
      if (beforeOpening && !isHistoryWindow(when)) continue;

      txns.push({
        key: `recurring-${rc.id}-${isoDate(when)}`,
        date: when,
        amount: -amountFor,
        isHistory: !!beforeOpening,
        label: rc.payee,
        category: rc.category || "other",
        source: {
          type: "recurring",
          id: rc.id,
          payee: rc.payee,
          frequency: rc.frequency,
          isVariable: rc.is_variable,
          note: rc.source_note,
          occurrenceDate: isoDate(when),
          standardAmount: spread ? round2((base * 12) / 52) : base,
          spread,
          monthlyAmount: base,
          adjusted: ov?.amount != null,
          adjustmentNote: ov?.note || null,
          paidDate: ov?.paid ? isoDate(when) : null,
        },
        // Marked as gone. It still belongs in its week — the money did leave —
        // but the grid can now show it settled rather than pending.
        isReceived: !!ov?.paid,
      });
    }
  }

  const allCharges = [
    ...amexCharges.map((c) => ({
      id: c.id,
      amount: Number(c.amount) || 0,
      charge_date: c.charge_date,
      description: c.description,
    })),
    ...amexFromSpend,
    ...amexFromRecurring,
  ];

  const amex = amexLedger({
    settings: amexSettings,
    charges: allCharges,
    payments: amexPayments,
    paydownOverrides,
    horizonEnd: end,
    today,
  });
  for (const p of amex.payments) {
    // A card payment made before the balance was struck is inside it, the same
    // as any other payment.
    const beforeOpening = openingDate && p.date <= openingDate;
    if (beforeOpening && !isHistoryWindow(p.date)) continue;

    txns.push({
      key: `amex-payment-${p.statementDate}`,
      date: p.date,
      amount: -p.amount,
      isHistory: !!beforeOpening,
      label: "AMEX payment",
      category: "amex",
      isReceived: !!p.paid,
      docKeys: p.docKeys || [],
      docAmounts: p.docAmounts || null,
      source: {
        type: "amex_payment",
        id: p.statementDate || isoDate(p.date),
        statementDate: p.statementDate,
        manual: !!p.manual,
        // A paydown the limit forced, rather than a statement falling due.
        // Carried so the drawer can offer to change or refuse it: the engine
        // can work out what the card needs, it cannot know what the bank can
        // stand, and until now the only way to say so was another screen.
        forced: !!p.forced,
        // The row behind it, where one exists, so the drawer can change it.
        paymentId: p.id || null,
        paymentDate: isoDate(p.date),
        paydownDate: p.forced ? isoDate(p.date) : null,
        wouldReach: p.wouldReach ?? null,
        limit: p.limit ?? null,
        note: p.note,
        amount: p.amount,
        docKeys: p.docKeys || [],
        docAmounts: p.docAmounts || null,
      },
    });
  }

  // Certain, or merely expected.
  //
  // The distinction people actually act on: a document exists and someone is
  // obliged by it, or it does not and this is a plan. An invoice raised and a
  // bill received are facts; a quote, a retainer not yet billed, a supplier
  // cost we intend to incur and a standing pattern are all forecasts, however
  // confident. A cost that delivers a quote is doubly so — it only happens if
  // the quote does.
  const COMMITTED = new Set([
    "scoro_invoice",
    "scoro_bill",
    "xero_bill",
    "xero_paid",
    "scoro_received",
    "on_account_payment",
    "instalment",
  ]);
  for (const t of txns) {
    const type = t.source?.type;
    const ridesOnAQuote = t.source?.attachedTo?.source === "scoro_quote";
    // A card repayment is only as certain as the charges behind it. One that
    // settles spend already made is an obligation; one settling a media budget
    // that has not been committed to yet is a forecast wearing a due date.
    const amexSettled =
      type === "amex_payment" && (t.isReceived || t.source?.manual || t.source?.enforced === true);
    t.committed =
      !t.isPipeline &&
      !ridesOnAQuote &&
      // A facility is not money. Lending counts once it has actually landed;
      // until then it is something available, which is a different claim.
      (t.isReceived || COMMITTED.has(type) || amexSettled || (type === "manual_entry" && !t.isLending));
    t.ridesOnAQuote = ridesOnAQuote;
  }

  txns.sort((a, b) => a.date - b.date);
  // Indexed for the drawer: opening a piece of income should show what it costs
  // to deliver without the UI having to work it out again.
  const spendByDoc = {};
  for (const sp of docSpend) {
    const key = `${sp.doc_source}:${sp.doc_id}`;
    (spendByDoc[key] = spendByDoc[key] || []).push(sp);
  }

  return { txns, anchor, end, amex, excluded, charges: allCharges, allocation, spendByDoc };
}

/**
 * Bucket into weeks and walk the balance.
 *
 * Bounds every index against the array length: incomplete settings should give
 * a slightly wrong balance, never a blank screen, which is how v1 failed.
 */
export function bucketByWeek(txns, { anchor, historyWeeks = 2, projectionWeeks = 26, openingBalance = 0, openingDateIso }) {
  const weeks = [];
  for (let i = -historyWeeks; i < projectionWeeks; i++) {
    const start = addWeeks(anchor, i);
    weeks.push({
      start,
      end: addDays(start, 6),
      isHistory: i < 0,
      txns: [],
      in: 0,
      out: 0,
    });
  }
  if (!weeks.length) return weeks;

  const startDay = epochDay(anchor);
  for (const t of txns) {
    const idx = Math.floor((epochDay(t.date) - startDay) / 7) + historyWeeks;
    if (idx < 0 || idx >= weeks.length) continue;
    weeks[idx].txns.push(t);
  }

  /**
   * Where the balance walk starts.
   *
   * The opening balance is a measured fact at its own date, and the walk has
   * to arrive back at exactly that figure on exactly that day. So movements
   * between the opening date and the first week shown are added on, and
   * movements inside the view dated before it are taken back off — the balance
   * already contains those.
   *
   * Taking them off is what lets the week the opening date falls inside still
   * show what moved. Anchoring to the bank each night puts the opening date
   * mid-week, so by Friday the whole of the current week sits behind it. The
   * week it was counted from showed £75,000 in and £141,000 out as a column of
   * nothing, because every movement in it was already banked.
   */
  const firstStart = weeks[0].start;
  const openingDate = parseDate(openingDateIso) || firstStart;
  let bal = Number(openingBalance) || 0;
  for (const t of txns) {
    if (t.date > openingDate && t.date < firstStart) bal += t.amount;
    if (t.date <= openingDate && t.date >= firstStart) bal -= t.amount;
  }

  for (const w of weeks) {
    w.startBal = bal;
    // Everything that moved, settled or not. What was already inside the
    // opening balance has been taken back off above, so counting it here puts
    // it back exactly once and the column still reconciles.
    w.in = w.txns.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
    w.out = w.txns.filter((t) => t.amount < 0).reduce((s, t) => s + t.amount, 0);
    // The part of the week that is already in the bank, so the grid can say
    // which of it is fact and which is still forecast.
    w.settledIn = w.txns.filter((t) => t.isHistory && t.amount > 0).reduce((s, t) => s + t.amount, 0);
    w.settledOut = w.txns.filter((t) => t.isHistory && t.amount < 0).reduce((s, t) => s + t.amount, 0);
    bal += w.in + w.out;
    w.endBal = bal;
  }
  return weeks;
}
